import React, { useState } from 'react';
import { ChevronRight, Settings, Plus } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import MatchRow, { DIVIDER, ROW_HOVER } from './MatchRow';
import MatchSettingsModal from '../MatchSettingsModal';
import AddMatchModal from '../AddMatchModal';
import { isEnabled, TOURNAMENT_ENTITIES } from '../../../tournaments/access';

const ROUND_HEADER_BG = 'bg-black/10 dark:bg-white/5 hover:bg-black/15 dark:hover:bg-white/10';

// One round — header (collapse chevron, label, game count, gear icon) + its
// list of MatchRows. Reused once per round by MatchTable, in both the
// mobile and desktop layouts (`variant` picks the DOM shape, same as
// MatchRow) — collapse state is owned by MatchTable and passed in so a
// round collapses in both layouts together, even though only one is ever
// visible at a time. `onMoveRow(index, direction)` (also from MatchTable)
// is threaded down to each row as concrete up/down handlers + whether that
// row is at either end of the round (nothing to swap with past the edges).
//
// The round's own gear icon opens the SAME MatchSettingsModal as a row's,
// scoped to 'round' (court + custom rules, no Team Pairing — there's no one
// sensible player choice across every match in a round). When this round
// already has rows with their own override, the modal shows an extra choice
// instead of one plain Apply — see MatchSettingsModal's `conflictingMatchCount`
// — and this component is what actually clears those rows' overrides when
// the organizer picks "override all", since only it knows this round's keys.
const RoundSection = ({
  round,
  rows,
  variant,
  isCollapsed,
  onToggle,
  onMoveRow,
  showCourt,
  courts,
  sportType,
  tournamentMatchSettings,
  roundOverride,
  onApplyRoundOverride,
  onApplyRowOverride,
  onOpenDetail,
  tournamentVO,
  currentUserId,
  canManageThisSchedule,
  updateMatchSchedule,
  deleteMatchRow,
  assignMatchParticipants,
  addMatch,
  fetchAllParticipants,
  participantType,
  startScoring,
  openLiveGame,
}) => {
  const { theme } = useTheme();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [addMatchOpen, setAddMatchOpen] = useState(false);
  const gameCount = `${rows.length} game${rows.length === 1 ? '' : 's'}`;
  const conflictingKeys = rows.filter((r) => r.hasRowOverride).map((r) => r.key);
  const canAddMatch = !!addMatch && isEnabled(currentUserId, TOURNAMENT_ENTITIES.MATCH_ADD, tournamentVO, { round });

  const emptyMessage = <>Determined by previous round results</>;

  const rowProps = (index) =>
    onMoveRow && {
      canMoveUp: index > 0,
      canMoveDown: index < rows.length - 1,
      onMoveUp: () => onMoveRow(index, -1),
      onMoveDown: () => onMoveRow(index, 1),
    };

  // No visual cue distinguished "this round has custom settings applied"
  // from "it doesn't" — a match row's own gear icon already does this
  // (row.hasRowOverride above), but the round-level one always rendered the
  // same muted color either way, so applying round settings looked like it
  // silently did nothing even when it correctly persisted.
  const gearButton = onApplyRoundOverride && (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setSettingsOpen(true);
      }}
      aria-label="Round settings"
      className={`shrink-0 rounded p-0.5 transition-colors ${roundOverride?.useCustomSettings ? theme.text : theme.textMuted} ${ROW_HOVER}`}
      title={roundOverride?.useCustomSettings ? 'Round settings (customized)' : 'Round settings'}
    >
      <Settings size={14} fill={roundOverride?.useCustomSettings ? 'currentColor' : 'none'} />
    </button>
  );

  const addMatchButton = canAddMatch && (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setAddMatchOpen(true);
      }}
      aria-label="Add match"
      className={`shrink-0 rounded p-0.5 transition-colors ${theme.textMuted} ${ROW_HOVER}`}
      title="Add match"
    >
      <Plus size={14} />
    </button>
  );

  const addMatchModal = canAddMatch && (
    <AddMatchModal
      isOpen={addMatchOpen}
      onClose={() => setAddMatchOpen(false)}
      round={round}
      participantType={participantType}
      fetchAllParticipants={fetchAllParticipants}
      onSave={addMatch}
    />
  );

  // Every row in this round with a real backend match (liveData.id) — passed
  // up so applyRoundOverride can persist the round's settings to EACH one's
  // tournament_rounds.overridden_match_settings, the same backend column a
  // per-match override already writes to (see applyRowOverride's doc
  // comment). Without this, a round-level override only ever lived in this
  // hook's local roundOverrides/localStorage state — real enough to resolve
  // this row's own settings display, but invisible to Start Scoring's actual
  // live game session, which loads its settings from a FRESH backend fetch
  // (LiveGameTab.jsx's tournamentScheduleAPI.getGame()), not from this
  // frontend's in-memory state — so it silently fell back to the tournament's
  // sport-wide default the moment scoring actually began.
  const rowLiveDataIds = rows.filter((r) => r.liveData?.id).map((r) => r.liveData.id);

  const settingsModal = onApplyRoundOverride && (
    <MatchSettingsModal
      isOpen={settingsOpen}
      onClose={() => setSettingsOpen(false)}
      scope="round"
      sportType={sportType}
      courts={courts}
      tournamentMatchSettings={tournamentMatchSettings}
      initialOverride={roundOverride}
      conflictingMatchCount={conflictingKeys.length}
      onApply={(override, applyToAllInRound) =>
        onApplyRoundOverride(round.id, override, applyToAllInRound ? conflictingKeys : [], rowLiveDataIds)
      }
    />
  );

  if (variant === 'mobile') {
    return (
      <div>
        <div className={`flex cursor-pointer items-center gap-2 px-3 py-2 transition-colors ${ROUND_HEADER_BG}`} onClick={onToggle}>
          <ChevronRight size={14} className={`flex-shrink-0 ${theme.textMuted} transition-transform ${isCollapsed ? '' : 'rotate-90'}`} />
          <span className={`text-xs font-medium ${theme.text}`}>{round.label}</span>
          <span className={`min-w-0 flex-1 truncate text-xs ${theme.textMuted}`}>· {gameCount}</span>
          {addMatchButton}
          {gearButton}
        </div>
        {!isCollapsed &&
          (rows.length > 0 ? (
            <div className="divide-y divide-gray-300 dark:divide-gray-600">
              {rows.map((row, index) => (
                <MatchRow
                  key={row.key}
                  row={row}
                  roundLabel={round.label}
                  variant="mobile"
                  showCourt={showCourt}
                  courts={courts}
                  sportType={sportType}
                  tournamentMatchSettings={tournamentMatchSettings}
                  onApplyRowOverride={onApplyRowOverride}
                  onOpenDetail={onOpenDetail && (() => onOpenDetail(row))}
                  tournamentVO={tournamentVO}
                  currentUserId={currentUserId}
                  canManageThisSchedule={canManageThisSchedule}
                  updateMatchSchedule={updateMatchSchedule}
                  deleteMatchRow={deleteMatchRow}
                  assignMatchParticipants={assignMatchParticipants}
                  fetchAllParticipants={fetchAllParticipants}
                  participantType={participantType}
                  startScoring={startScoring}
                  openLiveGame={openLiveGame}
                  {...rowProps(index)}
                />
              ))}
            </div>
          ) : (
            <div className={`px-3 py-3 text-center text-xs italic ${theme.textMuted}`}>{emptyMessage}</div>
          ))}
        {settingsModal}
        {addMatchModal}
      </div>
    );
  }

  return (
    <React.Fragment>
      <tr className={`cursor-pointer border-b ${DIVIDER} transition-colors ${ROUND_HEADER_BG}`} onClick={onToggle}>
        <td className={`border-r px-3 py-2 ${DIVIDER}`}>
          <ChevronRight size={14} className={`${theme.textMuted} transition-transform ${isCollapsed ? '' : 'rotate-90'}`} />
        </td>
        <td colSpan={showCourt ? 7 : 6} className="px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <div>
              <span className={`text-xs font-medium ${theme.text}`}>{round.label}</span>
              <span className={`ml-1 text-xs ${theme.textMuted}`}>· {gameCount}</span>
            </div>
            <div className="flex items-center gap-1">
              {addMatchButton}
              {gearButton}
            </div>
          </div>
        </td>
      </tr>
      {!isCollapsed &&
        (rows.length > 0 ? (
          rows.map((row, index) => (
            <MatchRow
              key={row.key}
              row={row}
              roundLabel={round.label}
              variant="desktop"
              showCourt={showCourt}
              courts={courts}
              sportType={sportType}
              tournamentMatchSettings={tournamentMatchSettings}
              onApplyRowOverride={onApplyRowOverride}
              onOpenDetail={onOpenDetail && (() => onOpenDetail(row))}
              tournamentVO={tournamentVO}
              currentUserId={currentUserId}
              canManageThisSchedule={canManageThisSchedule}
              updateMatchSchedule={updateMatchSchedule}
              deleteMatchRow={deleteMatchRow}
              assignMatchParticipants={assignMatchParticipants}
              fetchAllParticipants={fetchAllParticipants}
              participantType={participantType}
              startScoring={startScoring}
              openLiveGame={openLiveGame}
              {...rowProps(index)}
            />
          ))
        ) : (
          <tr className={`border-b ${DIVIDER} last:border-0`}>
            <td colSpan={showCourt ? 8 : 7} className={`px-3 py-3 text-center text-xs italic ${theme.textMuted}`}>
              {emptyMessage}
            </td>
          </tr>
        ))}
      {settingsModal}
      {addMatchModal}
    </React.Fragment>
  );
};

export default RoundSection;
